import { Injectable } from '@angular/core';

import { BehaviorSubject, combineLatest, debounceTime, distinctUntilChanged, map, startWith, takeUntil } from 'rxjs';

import { cloneDeep, ScreenBreakpointMapping, ScreenBreakpointPX } from '@summit/core';
import { AppConfig, FontSizes, LayoutState, SummitDisposing, UserConfig } from '@summit/models';

@Injectable({
  providedIn: 'root'
})
export class LayoutService extends SummitDisposing {
  // pipe(arg0: OperatorFunction<unknown, any>): import('rxjs').Observable<boolean> {
  //   throw new Error('Method not implemented.');
  // }
  private defaultConfig: AppConfig = {
    ripple: true,
    inputStyle: 'outlined',
    menuMode: 'static',
    colorScheme: 'light',
    theme: 'light-mode',
    scale: 14,
    screenSize: ScreenBreakpointPX.LG,
    overflowHandling: 'vertical',
    defaultPaginationRows: 250,
    colBreakpoint: [1, 2, 3, 4],
    blurPii: false,
    blurFinancial: false
  };

  private DesktopStart: number = 1025;

  private orientationBs = new BehaviorSubject<'portrait' | 'landscape'>('portrait');
  orientation$ = this.orientationBs.asObservable();
  private screenBreakpointBs = new BehaviorSubject<ScreenBreakpointPX>(ScreenBreakpointPX.LG);
  screenBreakpoint$ = this.screenBreakpointBs.asObservable().pipe(distinctUntilChanged());
  private screenSizeBs = new BehaviorSubject<{ x: number; y: number }>({ x: 0, y: 0 });
  screenSize$ = this.screenSizeBs.asObservable().pipe(debounceTime(100));
  mobile$ = this.screenSize$.pipe(map((size) => size.x < this.DesktopStart));
  desktop$ = this.screenSize$.pipe(map((size) => size.x >= this.DesktopStart));
  menuState$ = this.screenSize$.pipe(map((size) => (size.x < this.DesktopStart ? 'mobile' : 'desktop')));

  defaultState: LayoutState = {
    staticMenuDesktopInactive: true,
    overlayMenuActive: false,
    profileSidebarVisible: false,
    configSidebarVisible: false,
    staticMenuMobileActive: false,
    menuHoverActive: false,
    footerActive: false,
    helpActive: false,
    configActive: false,
    //cardWidth: '100%',
    sideBarVisible: true,
    screenSize: ScreenBreakpointPX.XS,
    //screenX: window.innerWidth,
    //screenY: window.innerHeight,

    //below layout states are saved
    darkMode: false,
    overflowHandling: 'vertical',
    sideBarToggle: false, //not sure if this is used for anything
    fontSize: 'normal',
    sideBarExpanded: true,
    blurPii: false,
    blurFinancial: false,
    scheduleBoardToolBar: 'remainingStats'
  };
  private stateBs = new BehaviorSubject<LayoutState>(this.defaultState);
  //HACK to keep things working
  get state() {
    return this.stateBs.value;
  }
  set state(value: LayoutState) {
    this.stateBs.next(value);
  }
  state$ = this.stateBs.asObservable();
  menuShow$ = combineLatest([this.state$, this.mobile$.pipe(startWith(true))]).pipe(
    map(
      ([state, _]) =>
        (this.isDesktop() && !state.staticMenuDesktopInactive) || (this.isMobile() && state.staticMenuMobileActive)
    ),
    distinctUntilChanged()
    // tap((event) => {
    //   console.log(`show menu`, event);
    // })
  );

  private configBs = new BehaviorSubject<AppConfig>(this.defaultConfig);
  config$ = this.configBs.asObservable(); //.pipe(

  private overlayOpen = new BehaviorSubject<boolean | null>(false);

  //   tap((event) => {
  //     console.log(event);
  //   })
  // );

  //added
  constructor() {
    super();
    const lclState = this.stateBs.value;
    // console.log('layout service', lclState, localStorage);
    const storedDarkMode = JSON.parse(localStorage?.getItem('currentDarkMode') ?? 'null');
    const storedOverFlow = JSON.parse(localStorage?.getItem('currentOverFlow') ?? 'null');
    const storedSideBarToggle = JSON.parse(localStorage?.getItem('currentSideBarToggle') ?? 'null');
    const storedFontSize = JSON.parse(localStorage?.getItem('currentFontSize') ?? 'null');
    const storedSideBarExpanded = JSON.parse(localStorage?.getItem('currentSideBarExpanded') ?? 'null');
    const storedBlurPii = JSON.parse(localStorage?.getItem('currentBlurPii') ?? 'null');
    const storedBlurFinancial = JSON.parse(localStorage?.getItem('currentBlurFinancial') ?? 'null');
    const storedUserSettingsRaw = localStorage?.getItem('currentUserSettings') ?? 'null';
    let storedUserSettings = null;
    if (storedUserSettingsRaw === 'undefined') {
      localStorage.removeItem('currentUserSettings');
    } else {
      storedUserSettings = JSON.parse(localStorage?.getItem('currentUserSettings') ?? 'null');
    }
    //console.log('storedUserSettings', storedUserSettings);
    lclState.scheduleBoardToolBar = localStorage?.getItem('currentScheduleBoardToolBar') ?? 'remainingStats';
    // console.log('layout service', lclState, localStorage);

    if (storedDarkMode != null) {
      lclState.darkMode = storedDarkMode;
      this.configBs.value.theme = storedDarkMode ? 'dark-mode' : 'light-mode';
    }
    if (storedOverFlow != null) {
      lclState.overflowHandling = storedOverFlow;
    }
    if (storedSideBarToggle != null) {
      lclState.sideBarToggle = storedSideBarToggle;
    }
    if (storedFontSize != null) {
      lclState.fontSize = storedFontSize;
    }
    if (storedSideBarExpanded != null) {
      lclState.sideBarExpanded = storedSideBarExpanded;
    }
    if (storedBlurFinancial != null) {
      lclState.blurFinancial = storedBlurFinancial;
    }
    if (storedBlurPii != null) {
      lclState.blurPii = storedBlurPii;
    }
    if (storedUserSettings != null) {
      lclState.userSettings = storedUserSettings;
    }

    if (lclState.overflowHandling != this.configBs.value.overflowHandling) {
      this.configBs.value.overflowHandling = lclState.overflowHandling;
    }
    this.stateBs.next(lclState);
    //console.log('layout service', this.stateBs.value);
    this.onConfigUpdate();

    //auto apply the mobile class to the body element
    this.mobile$.pipe(takeUntil(this.disposing$)).subscribe((mobile) => {
      const body = document.querySelector('body');
      if (mobile) {
        body?.classList.add('ebo-mobile');
      } else {
        body?.classList.remove('ebo-mobile');
      }
    });
  }

  overlayOpen$ = this.overlayOpen.asObservable();
  prefix = 'is-';
  sizes = [
    {
      id: ScreenBreakpointPX.XXL,
      name: 'xxl',
      css: `hidden d-xxl-block`
    },
    {
      id: ScreenBreakpointPX.XL,
      name: 'xl',
      css: `hidden d-xl-block`
    },
    {
      id: ScreenBreakpointPX.LG,
      name: 'lg',
      css: `hidden d-lg-block d-xl-none`
    },
    {
      id: ScreenBreakpointPX.MD,
      name: 'md',
      css: `hidden d-md-block d-lg-none`
    },
    {
      id: ScreenBreakpointPX.SM,
      name: 'sm',
      css: `hidden d-sm-block d-mhidden`
    },
    {
      id: ScreenBreakpointPX.XS,
      name: 'xs',
      css: `d-block d-sm-none`
    }
  ];

  updateState(): void {
    const lclState = this.stateBs.value;
    localStorage.setItem('currentDarkMode', JSON.stringify(lclState.darkMode));
    localStorage.setItem('currentOverFlow', JSON.stringify(lclState.overflowHandling));
    localStorage.setItem('currentSideBarToggle', JSON.stringify(lclState.sideBarToggle));
    localStorage.setItem('currentFontSize', JSON.stringify(lclState.fontSize));
    localStorage.setItem('currentSideBarExpanded', JSON.stringify(lclState.sideBarExpanded));
    localStorage.setItem('currentBlurPii', JSON.stringify(lclState.blurPii));
    localStorage.setItem('currentBlurFinancial', JSON.stringify(lclState.blurFinancial));
    if (lclState.scheduleBoardToolBar && lclState.scheduleBoardToolBar !== 'undefined')
      localStorage.setItem('currentScheduleBoardToolBar', lclState.scheduleBoardToolBar);
    else localStorage.removeItem('currentScheduleBoardToolBar');
    //console.log('saving currentUserSettings', lclState.userSettings);
    localStorage.setItem('currentUserSettings', JSON.stringify(lclState.userSettings));
    this.stateBs.next(lclState);
  }

  updateStateValue(key: string, value: unknown): void {
    const lclState = this.stateBs.value as LayoutState;
    lclState[key] = value as any;
    this.stateBs.next(lclState);
    this.updateState();
  }

  onMenuToggle() {
    //console.log('onMenuToggle', this.isOverlay(), this.isDesktop());
    // if (this.isOverlay()) {
    //   this.stateBs.value.overlayMenuActive = !this.stateBs.value.overlayMenuActive;
    //   if (this.stateBs.value.overlayMenuActive) {
    //     this.overlayOpen.next(null);
    //   }
    // } else
    if (this.isDesktop()) {
      this.stateBs.value.staticMenuDesktopInactive = !this.stateBs.value.staticMenuDesktopInactive;
    } else {
      this.stateBs.value.staticMenuMobileActive = !this.stateBs.value.staticMenuMobileActive;

      if (this.stateBs.value.staticMenuMobileActive) {
        this.overlayOpen.next(null);
      }
    }
    //console.log('onMenuToggle', this.stateBs.value.staticMenuDesktopInactive);
    this.stateBs.next(this.stateBs.value);
    this.resize();
  }

  showSidebar() {
    this.stateBs.value.staticMenuDesktopInactive = false;
    this.stateBs.next(this.stateBs.value);
    this.resize();
  }

  hideMenu() {
    this.stateBs.value.staticMenuDesktopInactive = true;
    this.stateBs.value.staticMenuMobileActive = false;
    //console.log('hide menu', this.stateBs.value.staticMenuDesktopInactive, this.stateBs.value.staticMenuMobileActive);
    this.stateBs.next(this.stateBs.value);
    this.resize();
  }

  showProfileSidebar() {
    this.stateBs.value.profileSidebarVisible = !this.stateBs.value.profileSidebarVisible;
    if (this.stateBs.value.profileSidebarVisible) {
      this.overlayOpen.next(null);
    }
  }

  showConfigSidebar() {
    this.stateBs.value.configSidebarVisible = true;
  }
  showConfigIcon() {
    this.stateBs.value.configActive = true;
  }

  isOverlay() {
    return this.configBs.value.menuMode === 'overlay';
  }

  isDesktop() {
    return window.innerWidth >= this.DesktopStart;
  }

  isMobile() {
    return !this.isDesktop();
  }
  onResize(_$event) {
    // if (debugBuild) console.log('INIT ', $event);
    // console.log(window.innerWidth);
    this.screenSizeBs.next({ x: window.innerWidth, y: window.innerHeight });
    this.detectScreenBreakpoint(window.innerWidth, window.innerHeight);
  }
  private detectScreenBreakpoint(innerWidth: number, innerHeight: number) {
    switch (true) {
      case innerWidth < ScreenBreakpointPX.SM:
        // colorConsole(`----> Screen X:${innerWidth} Y:${innerHeight} = ${ScreenBreakpointPX.XS} <-----`);
        this.stateBs.value.screenSize = ScreenBreakpointPX.XS;
        break;
      case innerWidth < ScreenBreakpointPX.MD:
        // colorConsole(`----> Screen X:${innerWidth} Y:${innerHeight} = ${ScreenBreakpointPX.SM} <-----`);
        this.stateBs.value.screenSize = ScreenBreakpointPX.SM;
        break;
      case innerWidth < ScreenBreakpointPX.LG:
        // colorConsole(`----> Screen X:${innerWidth} Y:${innerHeight} = ${ScreenBreakpointPX.MD} <-----`);
        this.stateBs.value.screenSize = ScreenBreakpointPX.MD;
        break;
      case innerWidth < ScreenBreakpointPX.XL:
        // colorConsole(`----> Screen X:${innerWidth} Y:${innerHeight} = ${ScreenBreakpointPX.LG} <-----`);
        this.stateBs.value.screenSize = ScreenBreakpointPX.LG;
        break;
      case innerWidth < ScreenBreakpointPX.XXL:
        // colorConsole(`----> Screen X:${innerWidth} Y:${innerHeight} = ${ScreenBreakpointPX.XL} <-----`);
        this.stateBs.value.screenSize = ScreenBreakpointPX.XL;
        break;
      case innerWidth >= ScreenBreakpointPX.XXL:
        // colorConsole(`----> Screen X:${innerWidth} Y:${innerHeight} = ${ScreenBreakpointPX.XXL} <-----`);
        this.stateBs.value.screenSize = ScreenBreakpointPX.XXL;
        break;
      default:
        this.stateBs.value.screenSize = ScreenBreakpointPX.XS;
    }
    this.screenBreakpointBs.next(this.stateBs.value.screenSize);
    this.orientationBs.next(innerWidth < innerHeight ? 'portrait' : 'landscape');

    if (this.stateBs.value.screenSize) {
      this.resize();
    } else {
      // console.log('todo error');
    }
  }
  onConfigUpdate(update?: AppConfig) {
    if (update) this.configBs.next(update);
    else this.configBs.next(this.configBs.value);
    // console.log('update', this.configUpdate.getValue());
  }
  async resize() {
    //console.log(`got Notice that it was resized to ${size} vs. ${this.currentUserExperienceSubject.value.ScreenBreakpoint}`);
    //if (debugBuild) console.log(`----> Comparing ${this.config.screenSize} to ${this.stateBs.value.screenSize} <-----`);
    const oldScreenSize = this.configBs.value.screenSize;
    const oldSideBarVisible = this.stateBs.value.sideBarVisible;

    this.configBs.value.blurFinancial = this.stateBs.value.blurFinancial;
    this.configBs.value.blurPii = this.stateBs.value.blurPii;
    this.configBs.value.screenSize = this.stateBs.value.screenSize;
    this.stateBs.value.sideBarVisible = this.isMobile() ? false : true;

    if (this.stateBs.value.sideBarVisible !== oldSideBarVisible || this.configBs.value.screenSize !== oldScreenSize) {
      this.onConfigUpdate({
        ...this.configBs.value,
        colBreakpoint: this.activeWidgetCols(this.stateBs.value.screenSize, this.stateBs.value.sideBarVisible),
        defaultPaginationRows: this.getDefaultPaginationRows(this.stateBs.value.screenSize)
      });
    }
  }

  protected activeWidgetCols(screenSize: ScreenBreakpointPX, sideBarVisible: boolean): number[] {
    // Should align with list-overflow-item in styles.css
    //console.log('activeWidgetCols', screenSize, sideBarVisible, this.overlayOpen.value);
    let response = [1];
    switch (screenSize) {
      case ScreenBreakpointPX.XXL:
        response = [1, 2, 3, 4];
        break;
      case ScreenBreakpointPX.XL:
        response = [1, 2, 3];
        break;
      case ScreenBreakpointPX.LG:
        response = this.isDesktop() && !this.stateBs.value.staticMenuDesktopInactive ? [1, 2] : [1, 2, 3];
        break;
      case ScreenBreakpointPX.MD:
        response = [1, 2];
        break;
      default:
        response = [1];
    }
    //console.log('activeWidgetCols', response);
    return response;
  }
  protected getAllowedColumnCount(screenSize: ScreenBreakpointPX): number {
    // Should align with list-overflow-item in styles.css
    switch (screenSize) {
      case ScreenBreakpointPX.XS:
        return 3;
      case ScreenBreakpointPX.SM:
        return 5;
      case ScreenBreakpointPX.MD:
        return 6;
      case ScreenBreakpointPX.LG:
        return 7;
      case ScreenBreakpointPX.XL:
        return 10;
      case ScreenBreakpointPX.XXL:
        return 12;
      default:
        return 6;
    }
  }
  protected getAgMaxColumnCount(screenSize: ScreenBreakpointPX) {
    // Should align with list-overflow-item in styles.css
    switch (screenSize) {
      case ScreenBreakpointPX.XS:
        return 3;
      case ScreenBreakpointPX.SM:
        return 6; //menu auto hides
      case ScreenBreakpointPX.MD:
        return 6;
      case ScreenBreakpointPX.LG:
        return 7;
      case ScreenBreakpointPX.XL:
        return 8;
      case ScreenBreakpointPX.XXL:
        return 14;
    }
  }
  protected getDefaultPaginationRows(screenSize: ScreenBreakpointPX) {
    switch (screenSize) {
      case ScreenBreakpointPX.XS:
        return 75;
      case ScreenBreakpointPX.SM:
        return 125;
      case ScreenBreakpointPX.MD:
        return 200;
      case ScreenBreakpointPX.LG:
        return 250;
      case ScreenBreakpointPX.XL:
        return 300;
    }
    return 200;
  }

  toggleHelp() {
    this.stateBs.value.helpActive = !this.stateBs.value.helpActive;
    this.stateBs.next(this.stateBs.value);
  }

  toggleSideBar() {
    this.stateBs.value.sideBarVisible = !this.stateBs.value.sideBarVisible;
    this.stateBs.next(this.stateBs.value);
  }
  changeDefaultFont(fontSize: FontSizes) {
    this.stateBs.value.fontSize = fontSize;
    this.stateBs.next(this.stateBs.value);
    this.updateState();
  }
  // changeCardWidth(cardWidth: 'fit-content' | '100%') {
  //   this.stateBs.value.cardWidth = cardWidth;
  // }
  toggleBlurPii() {
    const lclState = this.stateBs.value;
    lclState.blurPii = !lclState.blurPii;
    this.configBs.value.blurPii = lclState.blurPii;
    console.log(`New blur pii ${lclState.blurPii}`);
    this.stateBs.next(lclState);
    this.updateState();
    this.onConfigUpdate();
  }
  toggleBlurFinancial() {
    const lclState = this.stateBs.value;
    lclState.blurFinancial = !lclState.blurFinancial;
    this.configBs.value.blurFinancial = lclState.blurFinancial;
    console.log(`New blur financial ${lclState.blurFinancial}`);
    this.stateBs.next(lclState);
    this.updateState();
    this.onConfigUpdate();
  }

  toggleShowLightDarkToggle() {
    const lclState = this.stateBs.value;
    lclState.userSettings ??= {};
    lclState.userSettings.showLightDarkToggle = !lclState.userSettings?.showLightDarkToggle;
    // if (lclState.darkMode) this.changeTheme('dark-mode', 'dark');
    // else this.changeTheme('light-mode', 'light');
    this.stateBs.next(lclState);
    this.updateState();
  }

  toggleDarkMode() {
    const lclState = this.stateBs.value;
    lclState.darkMode = !lclState.darkMode;
    this.stateBs.next(lclState);
    this.updateState();
  }
  toggleOverflowHandling() {
    const lclState = this.stateBs.value;
    if (lclState.overflowHandling === 'vertical') {
      lclState.overflowHandling = 'horizontal';
    } else {
      lclState.overflowHandling = 'vertical';
    }
    //console.log(`New overflow handling ${this.stateBs.value.overflowHandling}`);
    this.onConfigUpdate({
      ...this.configBs.value,
      overflowHandling: lclState.overflowHandling
    });
    this.stateBs.next(lclState);
    this.updateState();
  }
  toggleSideBarExpand(toggle?: boolean) {
    const lclState = this.stateBs.value;
    if (toggle) {
      lclState.sideBarToggle = !lclState.sideBarToggle;
    } else {
      lclState.sideBarExpanded = !lclState.sideBarExpanded;
    }
    this.stateBs.next(lclState);
    this.updateState();
  }

  isScreenLargeOrBigger(): boolean {
    return this.isScreenLarger(ScreenBreakpointPX.LG);
  }
  isScreenSmOrSmaller(): boolean {
    return this.isScreenSmaller(ScreenBreakpointPX.MD);
  }
  isScreen(screenBreakpoint: ScreenBreakpointPX, greaterThan: boolean): boolean {
    //console.log('isScreen', this.config.screenSize, screenBreakpoint, greaterThan);
    return greaterThan
      ? ScreenBreakpointMapping[this.configBs.value.screenSize] >= ScreenBreakpointMapping[screenBreakpoint]
      : ScreenBreakpointMapping[this.configBs.value.screenSize] < ScreenBreakpointMapping[screenBreakpoint];
  }
  isScreenLarger(screenBreakpoint: ScreenBreakpointPX): boolean {
    return this.isScreen(screenBreakpoint, true);
  }
  isScreenSmaller(screenBreakpoint: ScreenBreakpointPX): boolean {
    //console.log('ux', this)
    return this.isScreen(screenBreakpoint, false);
  }

  getConfig(): AppConfig {
    return cloneDeep(this.configBs.value);
  }

  getState(): LayoutState {
    return cloneDeep(this.stateBs.value);
  }

  patchUserConfig(userConfig: Partial<UserConfig>) {
    const currentConfig = this.configBs.value;
    this.configBs.next({
      ...currentConfig,
      userSettings: {
        ...currentConfig.userSettings,
        ...userConfig
      }
    });
    this.stateBs.next({
      ...this.stateBs.value,
      userSettings: {
        ...this.stateBs.value.userSettings,
        ...userConfig
      }
    });
    this.updateState();
  }
}
